import { useMemo } from 'react';

export default function useTurniCalendar({ turniData, collaboratorsList, managerFerieList, selectedMonth }) {
  const displayNameMap = useMemo(() => {
    const allNames = new Set();
    collaboratorsList.forEach(c => {
      const full = `${c.nome || ''} ${c.cognome || ''}`.trim();
      if (full) allNames.add(full);
    });
    turniData.forEach(t => { if (t.operator_name) allNames.add(t.operator_name.trim()); });
    managerFerieList.forEach(l => { if (l.operator_name) allNames.add(l.operator_name.trim()); });

    const byFirstName = {};
    for (const name of allNames) {
      const firstName = name.split(/\s+/)[0];
      if (!byFirstName[firstName]) byFirstName[firstName] = [];
      byFirstName[firstName].push(name);
    }

    const map = new Map();
    for (const [firstName, names] of Object.entries(byFirstName)) {
      if (names.length === 1) {
        map.set(names[0], firstName);
      } else {
        for (const name of names) {
          const surname = name.trim().split(/\s+/).slice(1).join(' ');
          let displayName = `${firstName} ${surname}`;
          for (let i = 1; i <= surname.length; i++) {
            const prefix = surname.substring(0, i);
            const isUnique = names
              .filter(n => n !== name)
              .every(n => !n.trim().split(/\s+/).slice(1).join(' ').startsWith(prefix));
            if (isUnique) {
              displayName = `${firstName} ${prefix}`;
              break;
            }
          }
          map.set(name, displayName);
        }
      }
    }
    return map;
  }, [collaboratorsList, turniData, managerFerieList]);

  const getInitials = (fullName) => {
    if (!fullName) return '';
    const trimmed = fullName.trim();
    return displayNameMap.get(trimmed) || trimmed.split(/\s+/)[0];
  };

  const getBadgeProps = (type) => {
    switch (type) {
      case 'ferie':    return { color: 'green',  text: 'F' };
      case 'permesso': return { color: 'yellow', text: 'P' };
      case 'malattia': return { color: 'red',    text: 'M' };
      default:         return { color: 'blue',   text: 'T' };
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'coperto':  return 'green';
      case 'parziale': return 'yellow';
      case 'scoperto': return 'red';
      default:         return 'neutral';
    }
  };

  const [year, month] = selectedMonth.split('-');
  const daysCount = new Date(year, month, 0).getDate();
  let firstDay = new Date(year, month - 1, 1).getDay();
  firstDay = firstDay === 0 ? 6 : firstDay - 1;

  const actualDays = Array.from({ length: daysCount }, (_, i) => {
    const dateStr = `${selectedMonth}-${String(i + 1).padStart(2, '0')}`;
    const dayShifts = turniData.filter(t => t.date === dateStr);
    const standardShifts = dayShifts.filter(t => !['ferie', 'malattia', 'permesso'].includes(t.shift_type));
    const ferie = dayShifts.filter(t => t.shift_type === 'ferie');

    let totalHours = 0;
    standardShifts.forEach(shift => {
      if (shift.start_time && shift.end_time) {
        const [sH, sM] = shift.start_time.split(':').map(Number);
        const [eH, eM] = shift.end_time.split(':').map(Number);
        let duration = (eH + eM / 60) - (sH + sM / 60);
        if (duration < 0) duration += 24;
        totalHours += duration;
      }
    });

    let status = 'coperto';
    if (totalHours === 0 && standardShifts.length === 0) status = 'scoperto';
    else if (totalHours < 24) status = 'parziale';

    return {
      date: i + 1,
      fullDate: dateStr,
      status,
      totalHours,
      shifts: {
        standard: standardShifts.map(t => t.operator_name),
        ferie: ferie.map(t => t.operator_name),
        raw: dayShifts,
      },
    };
  });

  const paddingDays = Array.from({ length: firstDay }, () => null);
  const mockDays = paddingDays.concat(actualDays);

  const expectedMonthHours = actualDays.length * 24;
  let coveredMonthHours = 0;
  actualDays.forEach(day => { coveredMonthHours += Math.min(day.totalHours || 0, 24); });
  const coveragePercentage = expectedMonthHours > 0 ? Math.round((coveredMonthHours / expectedMonthHours) * 100) : 0;

  return {
    mockDays,
    getInitials,
    getBadgeProps,
    getStatusColor,
    coveragePercentage,
    coveredMonthHours,
    expectedMonthHours,
  };
}
